/**
 * Email Bulk Controller — signal-store-backed bulk actions on selected emails.
 *
 * Reads selectedIds from the central emailStore instead of its own idsValue.
 * Delegates counter display to the store's selectionCount computed signal.
 */

import { Controller } from '@hotwired/stimulus'
import { patch } from '@rails/request.js'
import { effect } from '@preact/signals'
import { listContext } from '../modules/email/list-context.js'

export default class extends Controller {
  static targets = ['counter']
  // Keep idsValue for backward compat (email-list syncs to it as a fallback)
  static values = { ids: Array }

  connect() {
    this._disposers = []
    const store = this._store()
    if (!store) return

    // Sync counter from store signal
    this._disposers.push(
      effect(() => {
        const count = store.selectionCount.value
        if (this.hasCounterTarget) {
          this.counterTarget.textContent = count > 0 ? `${count} selected` : ''
        }
      })
    )
  }

  disconnect() {
    this._disposers.forEach(d => d())
    this._disposers = []
  }

  // ── Bulk actions ──────────────────────────────────────────────────────────

  async archive()    { await this.performAction('archive') }
  async unarchive()  { await this.performAction('unarchive') }
  async trash()      { await this.performAction('trash') }
  async untrash()    { await this.performAction('untrash') }
  async markRead()   { await this.performAction('read') }
  async markUnread() { await this.performAction('unread') }
  async star()       { await this.performAction('star') }
  async unstar()     { await this.performAction('unstar') }

  async changeStatus(e) {
    await this.performAction('status', { status: e.params.status })
  }

  async assignResponsible(e) {
    await this.performAction('responsible', { responsible_id: e.params.responsibleId })
  }

  async addTag(e) {
    await this.performAction('tag_add', { tag_id: e.params.tagId })
  }

  async removeTag(e) {
    await this.performAction('tag_remove', { tag_id: e.params.tagId })
  }

  async performAction(bulkAction, extra = {}) {
    // Prefer store selectedIds for robustness; fall back to idsValue
    const store = this._store()
    const ids   = store ? store.selectedIds.value : this.idsValue
    if (!ids || ids.length === 0) return

    await patch('/emails/bulk/update', {
      body: JSON.stringify({
        ids,
        bulk_action: bulkAction,
        ...listContext(),
        ...extra
      }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })

    // Clear selection after bulk action
    if (store) store.clearSelection()
  }

  // Called by email-list when outlet sync is needed (backward compat)
  idsValueChanged() {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = this.idsValue.length > 0
        ? `${this.idsValue.length} selected`
        : ''
    }
  }

  _store() {
    return window._emailStore || null
  }
}
